import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

export type EyebrowTone = 'pohutukawa' | 'subtle' | 'onNgahere';

const EYEBROW_TONE: Record<EyebrowTone, string> = {
  pohutukawa: 'text-pohutukawa',
  subtle: 'text-subtle',
  onNgahere: 'text-ngahere-fore',
};

export interface EyebrowProps extends HTMLAttributes<HTMLParagraphElement> {
  tone?: EyebrowTone;
  children: ReactNode;
}

/** Uppercase kicker above a section heading — "Popular routes", "Impact". */
export function Eyebrow({ tone = 'pohutukawa', className, children, ...rest }: EyebrowProps) {
  return (
    <p
      className={cn(
        'm-0 text-caption font-semibold tracking-caps uppercase',
        EYEBROW_TONE[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </p>
  );
}

/** The uppercase divider label above a list — "Upcoming", "Past", "You". */
export function SectionLabel({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLHeadingElement> & { children: ReactNode }) {
  return (
    <h2
      className={cn('m-0 text-label font-bold tracking-eyebrow text-subtle uppercase', className)}
      {...rest}
    >
      {children}
    </h2>
  );
}

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /**
   * `hero` is the 54px home headline; `page` the 30px screen title; `panel`
   * the 24px trip route header; `section` the 21px publish step title;
   * `card` the 17px heading inside a panel.
   */
  level?: 'hero' | 'page' | 'panel' | 'section' | 'card';
  as?: 'h1' | 'h2' | 'h3' | 'h4';
  children: ReactNode;
}

const LEVEL: Record<NonNullable<HeadingProps['level']>, string> = {
  hero: 'text-hero font-bold leading-hero tracking-hero',
  page: 'text-display font-bold tracking-display',
  panel: 'text-heading font-bold tracking-display',
  section: 'text-heading-xs font-semibold',
  card: 'text-title-xs font-semibold',
};

const DEFAULT_TAG: Record<NonNullable<HeadingProps['level']>, 'h1' | 'h2' | 'h3'> = {
  hero: 'h1',
  page: 'h1',
  panel: 'h2',
  section: 'h2',
  card: 'h3',
};

export function Heading({ level = 'page', as, className, children, ...rest }: HeadingProps) {
  const Tag = as ?? DEFAULT_TAG[level];
  return (
    <Tag className={cn('m-0 font-display text-ink', LEVEL[level], className)} {...rest}>
      {children}
    </Tag>
  );
}

/** Hairline rule matching the internal dividers inside cards. */
export function Divider({ className, ...rest }: HTMLAttributes<HTMLHRElement>) {
  return <hr className={cn('m-0 h-px border-0 bg-line-soft', className)} {...rest} />;
}

export interface PriceProps {
  /** Already formatted — "$32". */
  amount: string;
  /** `list` is 24px on a search card; `detail` is 30px in the booking card. */
  size?: 'list' | 'detail' | 'compact';
  caption?: ReactNode;
  className?: string;
}

/** Seat price. Always pōhutukawa, always the display face, always extrabold. */
export function Price({ amount, size = 'list', caption, className }: PriceProps) {
  return (
    <div className={cn('text-right', className)}>
      <div
        className={cn(
          'font-display font-extrabold text-pohutukawa',
          size === 'detail' && 'text-display',
          size === 'list' && 'text-heading',
          size === 'compact' && 'text-title-xs',
        )}
      >
        {amount}
      </div>
      {caption ? <div className="text-mini text-subtle">{caption}</div> : null}
    </div>
  );
}
